import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Eye, EyeOff, Reply, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Panel } from "@/components/extranet/StatusBadge";
import { http, errMsg, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";

const TABS = [["pending", "Menunggu"], ["published", "Dipublikasikan"], ["", "Semua"]];

const ReplyBox = ({ r, onSaved, t }) => {
  const [text, setText] = useState(r.reply || "");
  const save = async () => {
    try { await http.put(`/extranet/reviews/${r.id}/reply`, { reply: text }); toast.success(t(text.trim() ? "Balasan disimpan" : "Balasan dihapus")); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 rounded-md bg-slate-50 p-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Reply className="h-3.5 w-3.5" /> {t("Balasan hotel (publik)")}</p>
      <Textarea data-testid={`ext-review-reply-input-${r.id}`} maxLength={1000} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Tulis balasan untuk tamu...")} className="bg-white text-sm" />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-slate-400">{text.length}/1000{r.status !== "published" ? ` · ${t("Tampil setelah review dipublikasikan")}` : ""}</span>
        <button data-testid={`ext-review-reply-save-${r.id}`} onClick={save} disabled={text === (r.reply || "")} className="rounded-md bg-[#1F5F8B] px-3 py-1.5 text-xs font-semibold text-white disabled:bg-slate-300">{t("Simpan balasan")}</button>
      </div>
    </div>
  );
};

const ReviewRow = ({ r, onPublish, onDelete, onSaved, t }) => (
  <div data-testid={`ext-review-${r.id}`} className="grid gap-3 p-5 md:grid-cols-[1fr_auto]">
    <div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded bg-[#1F5F8B] px-2 py-0.5 font-bold text-white">{r.rating}/10</span>
        <b>{r.guest_name}</b><span className="text-slate-500">{r.guest_email} · {r.booking_code} · {r.room_name}</span>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${r.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{t(r.status === "published" ? "Dipublikasikan" : "Menunggu")}</span>
      </div>
      {r.title && <p className="mt-2 font-semibold">{r.title}</p>}
      <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{r.comment}</p>
      <p className="mt-1 text-xs text-slate-400">{fmtDate(r.created_at.slice(0, 10))}</p>
      <ReplyBox key={r.reply || ""} r={r} onSaved={onSaved} t={t} />
    </div>
    <div className="flex items-start gap-2">
      <button data-testid={`ext-review-publish-${r.id}`} onClick={() => onPublish(r)} className="flex items-center gap-1.5 rounded-md bg-[#006CE4] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0052B4]">
        {r.status === "published" ? <><EyeOff className="h-4 w-4" /> {t("Sembunyikan")}</> : <><Eye className="h-4 w-4" /> {t("Publikasikan")}</>}
      </button>
      <button data-testid={`ext-review-delete-${r.id}`} onClick={() => onDelete(r)} className="rounded-md border border-red-300 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export default function Reviews() {
  const { property } = useOutletContext();
  const t = useT();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {} });
  const load = () => http.get(`/extranet/properties/${property.id}/reviews`, { params: { status: tab } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [property.id, tab]); // eslint-disable-line

  const onPublish = async (r) => {
    try { await http.post(`/extranet/reviews/${r.id}/publish`, { published: r.status !== "published" }); toast.success(t(r.status === "published" ? "Review disembunyikan" : "Review dipublikasikan")); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (r) => {
    if (!window.confirm(t("Hapus review ini secara permanen?"))) return;
    try { await http.delete(`/extranet/reviews/${r.id}`); toast.success(t("Review dihapus")); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{t("Review")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("Tamu otomatis menerima email permintaan review di hari check-out (14:00 WIB). Review baru tampil di situs setelah dipublikasikan.")}</p>
      </div>
      <div className="flex gap-2">
        {TABS.map(([k, l]) => (
          <button key={k || "all"} data-testid={`reviews-tab-${k || "all"}`} onClick={() => setTab(k)} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${tab === k ? "bg-[#1F5F8B] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>
            {t(l)}{k && data.counts[k] !== undefined ? ` (${data.counts[k]})` : ""}
          </button>
        ))}
      </div>
      <Panel title={t("Daftar review")} testid="reviews-panel">
        <div className="divide-y divide-slate-100">
          {data.items.length === 0 && <p data-testid="reviews-empty" className="p-8 text-center text-sm text-slate-500">{t("Belum ada review.")}</p>}
          {data.items.map((r) => <ReviewRow key={r.id} r={r} onPublish={onPublish} onDelete={onDelete} onSaved={load} t={t} />)}
        </div>
      </Panel>
    </div>
  );
}
